import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';

import LazyCardImage from './LazyCardImage';
import { renderRarityBadge } from '../utils/rarityBadge';
import { GameContext } from '../contexts/GameContext';
import constants from '../config';
import { resolveCardImage } from '../utils/resolveImage';
import { formatSealedType } from '../utils/sealedTypes';
import { arrangeCardsForGrid } from '../utils/arrangeGrid';

function resolveImageSrc(val) {
  if (!val) return null;
  if (val.indexOf('http') === 0) return val;
  return constants.DOMAIN + '/' + val;
}

// Legacy fallbacks — used when game config is not yet loaded
const LEGACY_SET_IMAGES = {
  'Awakened Primordials': 'https://shop.primaltcg.com/cdn/shop/files/TCGBoosterBox.png?v=1735836721',
  'Battlers of Bethesda': 'https://shop.primaltcg.com/cdn/shop/files/BB-GamefoundThumb-2.png?v=1735836738&width=1680',
  'ST04: Therian Origins': 'https://shop.primaltcg.com/cdn/shop/files/ST04-Sample.png?v=1744248404',
  'Warden of the Seven Deadly Sins': 'https://shop.primaltcg.com/cdn/shop/files/BoxThumb.png?v=1744247534&width=1680',
  'Zoltan Strike ST03': 'https://shop.primaltcg.com/cdn/shop/files/ST03Mock.png?v=1720049199',
  'ST01: Sea Invasion': 'uploads/2024-01-18T21:34:56.922ZPackDeck.png',
  'ST02: Slayer Guild': 'uploads/2024-01-19T01:01:57.065ZST2Pack.png',
  'Kickstarter Collector': 'uploads/2022-08-20T13:37:58.390ZKS-Col-booster-pack.png',
  'Primordial Promo Pack': 'uploads/2022-08-04T19:48:16.596Zprimordialpack.png',
  'Kickstarter': 'https://api.primaltcg.com/uploads/2023-04-28T15:05:14.489ZKS-booster-pack.png',
'2025 Crystal Cup': 'uploads/2026-02-23T20:00:00.000ZA0008.png',
  '2025 Online Play Promos': 'uploads/2026-02-23T20:00:00.000ZS0033F.png',
  '2026 Promos': 'uploads/2026-02-23T20:00:00.000ZA0005F.png',
  'Judge Promos': 'uploads/2026-02-23T21:00:00.000ZC0074J.png',
  'TP3 - Tournament Promos': 'uploads/2026-02-23T21:00:00.000ZC0204F.png',
  'TP4 - Tournament Promos': 'uploads/2026-02-23T21:00:00.000ZA0047F.png',
  'New Genesis': 'https://shop.primaltcg.com/cdn/shop/files/Set4BBMock.png?v=1762944777',
};

const LEGACY_COMING_SOON_SETS = [];

const LEGACY_SET_CATEGORIES = [
  { label: 'Booster Sets', sets: ['Awakened Primordials', 'Battlers of Bethesda', 'Warden of the Seven Deadly Sins', 'New Genesis'] },
  { label: 'Starter Decks', sets: ['ST01: Sea Invasion', 'ST02: Slayer Guild', 'Zoltan Strike ST03', 'ST04: Therian Origins'] },
  { label: 'Promo & Special', sets: ['2022 Promos', '2023 Online Championship Series', '2023 Promos', '2024 5K Promos', '2024 6-Pack Deluxe', '2024 Ambassador Promos', '2024 Gencon Promo', '2024 Halloween Promos', '2024 Promos', '2025 Anniversary Box Promos', '2025 Crystal Cup', '2025 Online Play Promos', '2026 Promos', 'Judge Promos', 'Kickstarter', 'Kickstarter Collector', 'Placement Promos', 'Pre-Order Promos', 'Primordial Promo Pack', 'Promo', 'TP 2 - Tournament Promos', 'TP3 - Tournament Promos', 'TP4 - Tournament Promos'] },
];

// Known set names for backward-compatible URL detection
const KNOWN_SET_NAMES = [
  'Awakened Primordials', 'Battlers of Bethesda', 'ST01: Sea Invasion',
  'ST02: Slayer Guild', 'ST04: Therian Origins',
  'Warden of the Seven Deadly Sins', 'Zoltan Strike ST03', 'Kickstarter',
  'Kickstarter Collector', 'Primordial Promo Pack', 'Promo',
  '2025 Anniversary Box Promos', '2024 Promos', '2024 5K Promos', '2024 Halloween Promos',
  '2024 Gencon Promo', '2024 6-Pack Deluxe', '2024 Ambassador Promos',
  '2023 Online Championship Series', 'Pre-Order Promos', 'Placement Promos',
  'TP 2 - Tournament Promos', '2022 Promos', '2023 Promos',
  '2025 Crystal Cup', '2025 Online Play Promos', '2026 Promos',
  'Judge Promos', 'TP3 - Tournament Promos', 'TP4 - Tournament Promos', 'New Genesis',
];

function fuzzyMatch(text, query) {
  text = text.toLowerCase();
  query = query.toLowerCase().trim();
  if (!query) return true;
  // Split query into words — each word must appear somewhere in text
  const words = query.split(/\s+/);
  return words.every(word => {
    // Check substring match first
    if (text.includes(word)) return true;
    // Fuzzy: allow 1 character difference per 3 chars (simple Levenshtein-like)
    if (word.length >= 3) {
      // Check if removing any single char from word matches a substring
      for (let i = 0; i < word.length; i++) {
        const partial = word.slice(0, i) + word.slice(i + 1);
        if (text.includes(partial)) return true;
      }
    }
    // Sequential character match (classic fuzzy)
    let ti = 0;
    for (let qi = 0; qi < word.length && ti < text.length; ti++) {
      if (text[ti] === word[qi]) qi++;
      if (qi === word.length) return true;
    }
    return false;
  });
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Name A\u2013Z' },
  { value: 'cardNumber', label: 'Card Number' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
];



class Browse extends Component {
  static contextType = GameContext;

  constructor(props) {
    super(props);
    this.state = {
      // Set grid view
      sets: [],
      setsLoading: true,

      // Cards in set view
      cards: [],
      sealedProducts: [],
      cardsLoading: false,
      cardsTotal: 0,
      skip: 0,
      limit: 20,

      // Filters
      rarity: '',
      element: '',
      cardType: '',
      search: '',

      // Sort
      sort: 'name',
      sortOpen: false,

      // Filter dropdown open states
      rarityOpen: false,
      elementOpen: false,
      cardTypeOpen: false,

      // Available filter options (derived from set data)
      elementOptions: [],
      cardTypeOptions: [],
      rarityOptions: [],

      // All cards in set (for fuzzy search)
      allSetCards: [],

      // Cross-set suggestions when no local results
      crossSetResults: [],
      crossSetLoading: false,

      // Scroll to top button
      showScrollTop: false,

      // Filter panel toggle (progressive disclosure)
      showFilters: false,
    };
    this.handleScroll = this.handleScroll.bind(this);
  }

  _getFilterCacheKey(setName) {
    return 'browse_filters_' + (setName || '');
  }

  _saveFilters() {
    var setName = this.props.match && this.props.match.params.setName;
    if (!setName) return;
    var filters = {
      rarity: this.state.rarity,
      element: this.state.element,
      cardType: this.state.cardType,
      search: this.state.search,
      sort: this.state.sort,
      showFilters: this.state.showFilters,
    };
    try { sessionStorage.setItem(this._getFilterCacheKey(setName), JSON.stringify(filters)); } catch (e) {}
  }

  _restoreFilters(setName) {
    try {
      var cached = sessionStorage.getItem(this._getFilterCacheKey(setName));
      if (cached) {
        var filters = JSON.parse(cached);
        return filters;
      }
    } catch (e) {}
    return null;
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
    this._handleThemeChanged = function() { this.forceUpdate(); }.bind(this);
    window.addEventListener('themeChanged', this._handleThemeChanged);

    const { gameSlug, setName } = (this.props.match && this.props.match.params) || {};

    // Backward compat: if gameSlug matches a known set name (old /browse/:setName URL), redirect
    if (gameSlug && !setName && KNOWN_SET_NAMES.includes(decodeURIComponent(gameSlug))) {
      const { currentGame } = this.context || {};
      const slug = (currentGame && currentGame.slug) || 'primal-tcg';
      this.props.history.replace(`/browse/${slug}/${gameSlug}`);
    }

    // Single-game: if at /browse with no gameSlug, redirect to /browse/:slug
    if (!gameSlug && !setName) {
      const { currentGame, isMultiGame } = this.context || {};
      if (currentGame && !isMultiGame) {
        this.props.history.replace(`/browse/${currentGame.slug}`);
      }
    }

    // Multi-game: load the selected game config when gameSlug is present
    if (gameSlug) {
      const { currentGame, setCurrentGame, games } = this.context || {};
      if (setCurrentGame && (!currentGame || currentGame.slug !== decodeURIComponent(gameSlug))) {
        var matchedGame = (games || []).find(function(g) { return g.slug === decodeURIComponent(gameSlug); });
        if (matchedGame) setCurrentGame(matchedGame);
      }
    }

    this._lastGameSlug = (this.context && this.context.currentGame && this.context.currentGame.slug) || null;

    if (setName) {
      var savedFilters = this._restoreFilters(setName);
      if (savedFilters) {
        this.setState({
          rarity: savedFilters.rarity || '',
          element: savedFilters.element || '',
          cardType: savedFilters.cardType || '',
          search: savedFilters.search || '',
          sort: savedFilters.sort || 'name',
          showFilters: savedFilters.showFilters || false,
        }, () => {
          this.fetchSetCards(decodeURIComponent(setName));
          this.fetchFilterOptions(decodeURIComponent(setName));
        });
      } else {
        this.fetchSetCards(decodeURIComponent(setName));
        this.fetchFilterOptions(decodeURIComponent(setName));
      }
    }
    this.fetchSets();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('themeChanged', this._handleThemeChanged);
    if (this._crossSetTimer) clearTimeout(this._crossSetTimer);
    this._saveFilters();
  }

  componentDidUpdate(prevProps, prevState, prevContext) {
    const prevParams = (prevProps.match && prevProps.match.params) || {};
    const currParams = (this.props.match && this.props.match.params) || {};
    const prevSet = prevParams.setName;
    const currSet = currParams.setName;

    // Backward compat: if gameSlug is actually a set name, redirect
    if (currParams.gameSlug && !currSet && KNOWN_SET_NAMES.includes(decodeURIComponent(currParams.gameSlug))) {
      const { currentGame } = this.context || {};
      const slug = (currentGame && currentGame.slug) || 'primal-tcg';
      this.props.history.replace(`/browse/${slug}/${currParams.gameSlug}`);
      return;
    }

    // Single-game: redirect /browse to /browse/:slug when context loads
    if (!currParams.gameSlug && !currSet) {
      const { currentGame, isMultiGame } = this.context || {};
      if (currentGame && !isMultiGame) {
        this.props.history.replace(`/browse/${currentGame.slug}`);
        return;
      }
    }

    // Multi-game: sync game config with the URL. Runs on every update (not
    // just slug changes) because on a fresh page load the games list arrives
    // from context after componentDidMount already ran.
    if (currParams.gameSlug) {
      const urlSlug = decodeURIComponent(currParams.gameSlug);
      const { currentGame, setCurrentGame, games } = this.context || {};
      if (setCurrentGame && (!currentGame || currentGame.slug !== urlSlug)) {
        var matchedGame = (games || []).find(function(g) { return g.slug === urlSlug; });
        if (matchedGame) setCurrentGame(matchedGame);
      }
    }

    // Refetch sets whenever the loaded game config changes (covers both
    // gameSlug navigation and the config arriving after a fresh load)
    const ctxGameSlug = (this.context && this.context.currentGame && this.context.currentGame.slug) || null;
    if (ctxGameSlug !== this._lastGameSlug) {
      this._lastGameSlug = ctxGameSlug;
      if (!currSet) {
        this.fetchSets();
      }
    }

    if (currSet !== prevSet) {
      if (currSet) {
        // Clear saved filters for the old set when navigating to a different set
        if (prevSet) {
          try { sessionStorage.removeItem(this._getFilterCacheKey(prevSet)); } catch (e) {}
        }
        this.setState({
          cards: [], sealedProducts: [], allSetCards: [], crossSetResults: [], crossSetLoading: false, skip: 0, rarity: '', element: '', cardType: '', search: '', sort: 'name', sortOpen: false,
        }, () => {
          this.fetchSetCards(decodeURIComponent(currSet));
          this.fetchFilterOptions(decodeURIComponent(currSet));
        });
      }
      window.scrollTo(0, 0);
    }

    // Safety net: if sets are still loading after redirect, fetch them
    if (!currSet && this.state.setsLoading) {
      this.fetchSets();
    }
  }

  handleScroll() {
    // Show/hide scroll-to-top button
    const showScrollTop = window.scrollY > 400;
    if (showScrollTop !== this.state.showScrollTop) {
      this.setState({ showScrollTop });
    }

    // Infinite scroll: load more when near bottom
    const { cardsLoading, cards, cardsTotal } = this.state;
    const setName = this.props.match && this.props.match.params.setName;
    if (!setName || cardsLoading || cards.length >= cardsTotal || cards.length === 0 || this.state.search) return;

    const scrollBottom = window.innerHeight + window.scrollY;
    const threshold = document.documentElement.scrollHeight - 300;
    if (scrollBottom >= threshold) {
      this.fetchSetCards(decodeURIComponent(setName), true);
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  fetchSets() {
    const gameSlug = (this.context && this.context.currentGame && this.context.currentGame.slug) || null;
    // Dedupe identical in-flight requests, but let a different game supersede
    // one that's still loading (e.g. unfiltered mount fetch vs. post-config fetch)
    if (this._fetchingSets && this._fetchingSetsSlug === gameSlug) return;
    this._fetchingSets = true;
    this._fetchingSetsSlug = gameSlug;
    const seq = (this._setsFetchSeq = (this._setsFetchSeq || 0) + 1);
    const gameParam = gameSlug ? `?game=${encodeURIComponent(gameSlug)}` : '';
    fetch(`${constants.DOMAIN}/api/v1/guest/sets${gameParam}`, { mode: 'cors', method: 'GET' })
      .then(r => r.json())
      .then(res => {
        if (seq !== this._setsFetchSeq) return; // stale response, a newer fetch superseded this one
        this._fetchingSets = false;
        this.setState({ sets: res.sets || [], setsLoading: false });
      })
      .catch(err => {
        if (seq !== this._setsFetchSeq) return;
        this._fetchingSets = false;
        console.log('Error fetching sets:', err);
        this.setState({ setsLoading: false });
      });
  }

  fetchFilterOptions(setName) {
    fetch(`${constants.DOMAIN}/api/v1/guest/sets/${encodeURIComponent(setName)}/cards?limit=500`, { mode: 'cors', method: 'GET' })
      .then(r => r.json())
      .then(res => {
        const elems = new Set();
        const types = new Set();
        const rarities = new Set();
        (res.cards || []).forEach(c => {
          if (c.element && Array.isArray(c.element)) {
            c.element.forEach(e => elems.add(e));
          }
          if (c.cardType) {
            types.add(c.cardType);
          }
          if (c.rarity) {
            rarities.add(c.rarity);
          }
        });
        this.setState({
          allSetCards: res.cards || [],
          elementOptions: Array.from(elems).sort(),
          cardTypeOptions: Array.from(types).sort(),
          rarityOptions: Array.from(rarities).sort(),
        });
      })
      .catch(() => {});
  }

  fetchSetCards(setName, append) {
    const { rarity, element, cardType, search, sort, skip, limit } = this.state;
    const currentSkip = append ? skip : 0;

    const params = new URLSearchParams();
    if (rarity) params.set('rarity', rarity);
    if (element) params.set('element', element);
    if (cardType) params.set('cardType', cardType);
    if (search) params.set('search', search);
    if (sort) params.set('sort', sort);
    params.set('limit', limit);
    params.set('skip', currentSkip);

    this.setState({ cardsLoading: true });

    fetch(`${constants.DOMAIN}/api/v1/guest/sets/${encodeURIComponent(setName)}/cards?${params.toString()}`, {
      mode: 'cors', method: 'GET',
    })
      .then(r => r.json())
      .then(res => {
        var update = {
          cards: append ? this.state.cards.concat(res.cards || []) : (res.cards || []),
          cardsTotal: res.total || 0,
          cardsLoading: false,
          skip: currentSkip + (res.cards || []).length,
        };
        // Only set sealed products on first load (not append/load-more)
        if (!append && res.sealedProducts) {
          update.sealedProducts = res.sealedProducts;
        }
        this.setState(update);
      })
      .catch(err => {
        console.log('Error fetching set cards:', err);
        this.setState({ cardsLoading: false });
      });
  }

  handleFilterChange(field, value) {
    const setName = this.props.match && this.props.match.params.setName;
    if (field === 'search') {
      // Client-side fuzzy search
      this.setState({ search: value, crossSetResults: [], crossSetLoading: false }, () => {
        this._saveFilters();
        // If search has a value but no local results, search across all sets
        if (value && value.trim()) {
          const localResults = this.allSetCards_filter(value);
          if (localResults.length === 0) {
            this.fetchCrossSetResults(value);
          }
        }
      });
    } else {
      const { search: currentSearch } = this.state;
      if (currentSearch) {
        // If search is active, just update the filter — getFilteredCards() applies both
        this.setState({ [field]: value, crossSetResults: [], crossSetLoading: false }, () => {
          this._saveFilters();
        });
      } else {
        this.setState({ [field]: value, skip: 0, crossSetResults: [], crossSetLoading: false }, () => {
          this._saveFilters();
          if (setName) this.fetchSetCards(decodeURIComponent(setName));
        });
      }
    }
  }

  allSetCards_filter(query) {
    const { allSetCards, rarity, element, cardType } = this.state;
    return allSetCards.filter(card => {
      if (!fuzzyMatch(card.name, query)) return false;
      if (rarity && card.rarity !== rarity) return false;
      if (element && (!card.element || !card.element.includes(element))) return false;
      if (cardType && card.cardType !== cardType) return false;
      return true;
    });
  }

  fetchCrossSetResults(query) {
    if (this._crossSetTimer) clearTimeout(this._crossSetTimer);
    this._crossSetTimer = setTimeout(() => {
      this.setState({ crossSetLoading: true });
      // Use a short prefix (first 3 chars of first word) to get candidates from API,
      // then fuzzy-filter client-side — the API only does exact substring matching
      const words = query.trim().split(/\s+/);
      const prefix = words[0].substring(0, Math.min(3, words[0].length));
      const gameSlug = this.context && this.context.currentGame && this.context.currentGame.slug;
      const gameParam = gameSlug ? `&game=${encodeURIComponent(gameSlug)}` : '';
      fetch(`${constants.DOMAIN}/api/v1/guest/cards/search?search=${encodeURIComponent(prefix)}${gameParam}`, { mode: 'cors', method: 'GET' })
        .then(r => r.json())
        .then(res => {
          if (this.state.search === query) {
            const currentSet = this.props.match && this.props.match.params.setName
              ? decodeURIComponent(this.props.match.params.setName) : '';
            // Fuzzy-filter the API results and exclude cards from current set
            const otherSetCards = (res.cards || [])
              .filter(c => c.cardSet !== currentSet && fuzzyMatch(c.name, query));
            this.setState({ crossSetResults: otherSetCards, crossSetLoading: false });
          }
        })
        .catch(() => {
          this.setState({ crossSetResults: [], crossSetLoading: false });
        });
    }, 300);
  }

  getFilteredCards() {
    const { cards, allSetCards, search, rarity, element, cardType } = this.state;
    if (!search || !search.trim()) return cards;
    // Fuzzy search across all cards in the set, combined with active filters
    return allSetCards.filter(card => {
      if (!fuzzyMatch(card.name, search)) return false;
      if (rarity && card.rarity !== rarity) return false;
      if (element && (!card.element || !card.element.includes(element))) return false;
      if (cardType && card.cardType !== cardType) return false;
      return true;
    });
  }

  // renderRarityBadge imported from utils/rarityBadge

  renderGameGrid() {
    const { games } = this.context || {};
    if (!games || !games.length) return null;
    return (
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6">
        {games.map(function(game) {
          return (
            <Link
              key={game._id}
              to={'/browse/' + encodeURIComponent(game.slug)}
              className="browse-set-hover tw-block tw-rounded-xl tw-border tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-bg-white dark:tw-bg-[#1a1a1a] tw-overflow-hidden tw-no-underline tw-transition-all tw-duration-200"
            >
              <div className="tw-aspect-[16/9] tw-bg-[#f0f0f0] dark:tw-bg-[#252525] tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
                {game.logo ? (
                  <img src={resolveImageSrc(game.logo)} alt={game.name} className="tw-w-full tw-h-full tw-object-contain tw-p-4" />
                ) : (
                  <i className="fa fa-gamepad tw-text-5xl tw-text-[#999] dark:tw-text-gray-600" />
                )}
              </div>
              <div className="tw-p-4">
                <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1">{game.name}</h3>
                <span className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500">Browse sets &rarr;</span>
              </div>
            </Link>
          );
        })}
      </div>
    );
  }

  renderSetGrid() {
    const { sets, setsLoading } = this.state;

    if (setsLoading) {
      return (
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-5">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-overflow-hidden">
              <div className="tw-aspect-[4/5] hp-skeleton" />
              <div className="tw-p-4">
                <div className="tw-h-5 tw-w-3/4 hp-skeleton tw-rounded tw-mb-2" />
                <div className="tw-h-4 tw-w-1/3 hp-skeleton tw-rounded" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (sets.length === 0) {
      return (
        <p className="tw-text-center tw-text-[#999] dark:tw-text-gray-500 tw-text-sm tw-mt-12">
          No card sets found.
        </p>
      );
    }

    // Get config from GameContext, falling back to legacy hardcoded values
    const { currentGame } = this.context || {};
    const setImages = (currentGame && currentGame.setImages) || LEGACY_SET_IMAGES;
    const comingSoonSets = (currentGame && currentGame.comingSoonSets) || LEGACY_COMING_SOON_SETS;
    const setCategories = (currentGame && currentGame.setCategories) || LEGACY_SET_CATEGORIES;
    const gameSlug = (currentGame && currentGame.slug) || (this.props.match && this.props.match.params.gameSlug) || 'primal-tcg';

    const setMap = {};
    sets.forEach(s => { setMap[s.name] = s; });

    const comingSoonMap = {};
    comingSoonSets.forEach(s => { comingSoonMap[s.name] = s; });

    return (
      <div>
        {setCategories.map(cat => {
          const catSets = cat.sets.filter(name => setMap[name]);
          const catComingSoon = (cat.comingSoon || []).filter(name => comingSoonMap[name]);
          if (catSets.length === 0 && catComingSoon.length === 0) return null;

          return (
            <div key={cat.label} className="tw-mb-12">
              <h3 className="tw-text-xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-5">
                {cat.label}
              </h3>
              <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-5">
                {catSets.map(name => {
                  const set = setMap[name];
                  const imageUrl = resolveImageSrc(setImages[set.name]) || (set.image ? (
                    set.image.startsWith('http') ? set.image : `${constants.CARD_IMAGE_BASE}${set.image}`
                  ) : null);

                  return (
                    <Link
                      key={set.name}
                      to={`/browse/${encodeURIComponent(gameSlug)}/${encodeURIComponent(set.name)}`}
                      className="tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-solid tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-overflow-hidden tw-transition-all tw-duration-200 browse-set-hover"
                      style={{ display: 'block', textDecoration: 'none' }}
                    >
                      <div className="tw-aspect-[4/5] tw-bg-[#f0f0f0] dark:tw-bg-[#252525] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-p-3 tw-relative">
                        <img src={imageUrl} alt={set.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      </div>
                      <div className="tw-p-4">
                        <p className="tw-text-sm tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1 tw-truncate">
                          {set.name}
                        </p>
                        <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-mb-0 tw-flex tw-items-center tw-gap-1">
                          <svg className="tw-w-3 tw-h-3 tw-opacity-50" viewBox="0 0 20 20" fill="currentColor"><path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z"/></svg>
                          {set.cardCount} card{set.cardCount !== 1 ? 's' : ''}
                          {set.sealedCount > 0 && (
                            <span> · {set.sealedCount} sealed</span>
                          )}
                        </p>
                      </div>
                    </Link>
                  );
                })}
                {catComingSoon.map(name => {
                  const set = comingSoonMap[name];
                  return (
                    <div
                      key={set.name}
                      className="tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-solid tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-overflow-hidden tw-relative"
                      style={{ opacity: 0.85 }}
                    >
                      <div className="tw-aspect-[4/5] tw-bg-[#f0f0f0] dark:tw-bg-[#252525] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-p-3 tw-relative">
                        <img src={resolveImageSrc(set.image)} alt={set.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        <div
                          className="tw-absolute tw-top-3 tw-right-3 tw-text-[10px] tw-font-bold tw-px-2.5 tw-py-1 tw-rounded-full tw-text-black tw-uppercase tw-tracking-wide"
                          style={{ background: 'linear-gradient(135deg, #c9a84c, #b8953f)' }}
                        >
                          Coming Soon
                        </div>
                      </div>
                      <div className="tw-p-4">
                        <p className="tw-text-sm tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1 tw-truncate">
                          {set.name}
                        </p>
                        <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-mb-0 tw-flex tw-items-center tw-gap-1">
                          <svg className="tw-w-3 tw-h-3 tw-opacity-50" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/></svg>
                          Coming Soon
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderFilterBar() {
    const { rarity, element, cardType, search, sort, sortOpen, elementOptions, showFilters } = this.state;
    const hasActiveFilters = !!(rarity || element || cardType);
    const isDark = document.documentElement.classList.contains('tw-dark');
    var defaultBorderColor = isDark ? '#555' : '#e5e5e5';
    const dropdownStyle = Object.assign({
      fontSize: '16px',
    }, isDark
      ? { backgroundColor: '#0f0f0f', color: '#fff', border: '1px solid #555' }
      : { backgroundColor: '#fff', color: '#1a1a1a', border: '1px solid #e5e5e5' });

    return (
      <div className="tw-mb-6">
        {/* Top row: Search + Filters toggle + Sort */}
        <div className="tw-flex tw-flex-wrap tw-gap-3">
          {/* Search */}
          <div
            className="tw-flex tw-items-center tw-flex-1 tw-min-w-[180px]"
            style={isDark
              ? { border: '1px solid #555', borderRadius: '8px', height: '38px', backgroundColor: '#0f0f0f' }
              : { border: '1px solid #e5e5e5', borderRadius: '8px', height: '38px', backgroundColor: '#fff' }}
          >
            <span className="tw-text-gray-400 tw-text-sm" style={{ paddingLeft: '10px' }}>
              <i className="fa fa-search" />
            </span>
            <input
              type="text"
              value={search}
              onChange={e => this.handleFilterChange('search', e.target.value)}
              placeholder="Search cards..."
              className="tw-flex-1 tw-bg-transparent tw-text-[#1a1a1a] dark:tw-text-white tw-text-sm tw-outline-none tw-placeholder-gray-500"
              style={{ border: 'none', padding: '0 10px', height: '100%', boxShadow: 'none' }}
            />
            {search && (
              <button
                onClick={() => this.setState({ search: '', crossSetResults: [], crossSetLoading: false })}
                className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-gray-400 hover:tw-text-[#1a1a1a] dark:hover:tw-text-white tw-text-sm tw-px-2"
                style={{ lineHeight: 1 }}
              >
                &times;
              </button>
            )}
          </div>

          {/* Filters toggle button */}
          {(this.state.rarityOptions.length > 0 || elementOptions.length > 0 || this.state.cardTypeOptions.length > 0) && (
            <button
              onClick={() => this.setState({ showFilters: !showFilters })}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-cursor-pointer tw-transition-colors tw-border-0 hover:tw-border-brand-gold"
              style={isDark
                ? { backgroundColor: '#0f0f0f', color: '#fff', border: '1px solid ' + (showFilters ? '#c9a84c' : '#555') }
                : { backgroundColor: '#fff', color: '#1a1a1a', border: '1px solid ' + (showFilters ? '#c9a84c' : '#e5e5e5') }}
            >
              <svg className="tw-w-4 tw-h-4 tw-text-[#999]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span>Filters</span>
              {hasActiveFilters && (
                <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-brand-gold tw-flex-shrink-0" />
              )}
            </button>
          )}

          {/* Sort dropdown */}
          <div className="tw-relative">
            <button
              onClick={() => this.setState({ sortOpen: !sortOpen, rarityOpen: false, elementOpen: false, cardTypeOpen: false })}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-cursor-pointer tw-transition-colors hover:tw-border-brand-gold"
              style={isDark
                ? { backgroundColor: '#0f0f0f', color: '#fff', border: '1px solid #555' }
                : { backgroundColor: '#fff', color: '#1a1a1a', border: '1px solid #e5e5e5' }}
            >
              <span className="tw-text-[#999] dark:tw-text-gray-500">Sort:</span>
              <span>{SORT_OPTIONS.find(o => o.value === sort)?.label || 'Name A\u2013Z'}</span>
              <svg className="tw-w-3.5 tw-h-3.5 tw-text-[#999]" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            {sortOpen && (
              <>
                <div className="tw-fixed tw-inset-0 tw-z-10" onClick={() => this.setState({ sortOpen: false })} />
                <div className="dropdown-enter tw-absolute tw-left-0 sm:tw-left-auto sm:tw-right-0 tw-top-full tw-mt-1.5 tw-z-20 tw-rounded-xl tw-py-1.5 tw-min-w-[200px]" style={isDark ? { backgroundColor: 'rgba(30,30,30,0.95)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' } : { backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
                  {SORT_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        this.setState({ sortOpen: false });
                        this.handleFilterChange('sort', opt.value);
                      }}
                      className="tw-w-full tw-text-left tw-px-4 tw-py-2.5 tw-text-sm tw-cursor-pointer tw-border-0 tw-flex tw-items-center tw-justify-between tw-bg-transparent"
                      style={sort === opt.value ? { color: '#c9a84c', fontWeight: 600 } : (isDark ? { color: '#fff' } : { color: '#1a1a1a' })}
                    >
                      <span>{opt.label}</span>
                      {sort === opt.value && <svg className="tw-w-4 tw-h-4" style={{ color: '#c9a84c' }} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Collapsible filter dropdowns */}
        <div className={'collapsible' + (showFilters ? ' collapsible-expanded' : '')}>
          <div className="tw-flex tw-flex-wrap tw-gap-3 tw-mt-3">
            {this.state.rarityOptions.length > 0 && (
              <div className="tw-relative">
                <button
                  onClick={() => this.setState(function(prev) { return { rarityOpen: !prev.rarityOpen, elementOpen: false, cardTypeOpen: false }; })}
                  className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-5 tw-py-3 tw-cursor-pointer tw-transition-colors"
                  style={Object.assign({}, dropdownStyle, { borderColor: rarity ? '#c9a84c' : defaultBorderColor })}
                >
                  <span>{rarity || 'All Rarities'}</span>
                  <svg className="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" style={{ color: '#999', transition: 'transform 200ms ease', transform: this.state.rarityOpen ? 'rotate(180deg)' : 'rotate(0)' }} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {this.state.rarityOpen && (
                  <>
                    <div className="tw-fixed tw-inset-0 tw-z-10" onClick={() => this.setState({ rarityOpen: false })} />
                    <div className="dropdown-enter tw-absolute tw-left-0 tw-top-full tw-mt-1.5 tw-z-20 tw-rounded-xl tw-py-1.5 tw-min-w-[200px]" style={isDark ? { backgroundColor: 'rgba(30,30,30,0.95)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' } : { backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
                      <button
                        onClick={() => { this.setState({ rarityOpen: false }); this.handleFilterChange('rarity', ''); }}
                        className="tw-w-full tw-text-left tw-px-4 tw-py-2.5 tw-text-sm tw-cursor-pointer tw-border-0 tw-flex tw-items-center tw-justify-between tw-bg-transparent"
                        style={!rarity ? { color: '#c9a84c', fontWeight: 600 } : (isDark ? { color: '#fff' } : { color: '#1a1a1a' })}
                      >
                        <span>All Rarities</span>
                        {!rarity && <svg className="tw-w-4 tw-h-4" style={{ color: '#c9a84c' }} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                      </button>
                      {this.state.rarityOptions.map(function(r) {
                        var isActive = rarity === r;
                        return (
                          <button
                            key={r}
                            onClick={function() { this.setState({ rarityOpen: false }); this.handleFilterChange('rarity', r); }.bind(this)}
                            className="tw-w-full tw-text-left tw-px-4 tw-py-2.5 tw-text-sm tw-cursor-pointer tw-border-0 tw-flex tw-items-center tw-justify-between tw-bg-transparent"
                            style={isActive ? { color: '#c9a84c', fontWeight: 600 } : (isDark ? { color: '#fff' } : { color: '#1a1a1a' })}
                          >
                            <span>{r}</span>
                            {isActive && <svg className="tw-w-4 tw-h-4" style={{ color: '#c9a84c' }} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                          </button>
                        );
                      }.bind(this))}
                    </div>
                  </>
                )}
              </div>
            )}

            {elementOptions.length > 0 && (
              <div className="tw-relative">
                <button
                  onClick={() => this.setState(function(prev) { return { elementOpen: !prev.elementOpen, rarityOpen: false, cardTypeOpen: false }; })}
                  className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-5 tw-py-3 tw-cursor-pointer tw-transition-colors"
                  style={Object.assign({}, dropdownStyle, { borderColor: element ? '#c9a84c' : defaultBorderColor })}
                >
                  <span>{element || 'All Elements'}</span>
                  <svg className="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" style={{ color: '#999', transition: 'transform 200ms ease', transform: this.state.elementOpen ? 'rotate(180deg)' : 'rotate(0)' }} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {this.state.elementOpen && (
                  <>
                    <div className="tw-fixed tw-inset-0 tw-z-10" onClick={() => this.setState({ elementOpen: false })} />
                    <div className="dropdown-enter tw-absolute tw-left-0 tw-top-full tw-mt-1.5 tw-z-20 tw-rounded-xl tw-py-1.5 tw-min-w-[200px]" style={isDark ? { backgroundColor: 'rgba(30,30,30,0.95)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' } : { backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
                      <button
                        onClick={() => { this.setState({ elementOpen: false }); this.handleFilterChange('element', ''); }}
                        className="tw-w-full tw-text-left tw-px-4 tw-py-2.5 tw-text-sm tw-cursor-pointer tw-border-0 tw-flex tw-items-center tw-justify-between tw-bg-transparent"
                        style={!element ? { color: '#c9a84c', fontWeight: 600 } : (isDark ? { color: '#fff' } : { color: '#1a1a1a' })}
                      >
                        <span>All Elements</span>
                        {!element && <svg className="tw-w-4 tw-h-4" style={{ color: '#c9a84c' }} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                      </button>
                      {elementOptions.map(function(el) {
                        var isActive = element === el;
                        return (
                          <button
                            key={el}
                            onClick={function() { this.setState({ elementOpen: false }); this.handleFilterChange('element', el); }.bind(this)}
                            className="tw-w-full tw-text-left tw-px-4 tw-py-2.5 tw-text-sm tw-cursor-pointer tw-border-0 tw-flex tw-items-center tw-justify-between tw-bg-transparent"
                            style={isActive ? { color: '#c9a84c', fontWeight: 600 } : (isDark ? { color: '#fff' } : { color: '#1a1a1a' })}
                          >
                            <span>{el}</span>
                            {isActive && <svg className="tw-w-4 tw-h-4" style={{ color: '#c9a84c' }} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                          </button>
                        );
                      }.bind(this))}
                    </div>
                  </>
                )}
              </div>
            )}

            {this.state.cardTypeOptions.length > 0 && (
              <div className="tw-relative">
                <button
                  onClick={() => this.setState(function(prev) { return { cardTypeOpen: !prev.cardTypeOpen, rarityOpen: false, elementOpen: false }; })}
                  className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-5 tw-py-3 tw-cursor-pointer tw-transition-colors"
                  style={Object.assign({}, dropdownStyle, { borderColor: cardType ? '#c9a84c' : defaultBorderColor })}
                >
                  <span>{cardType || 'All Types'}</span>
                  <svg className="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" style={{ color: '#999', transition: 'transform 200ms ease', transform: this.state.cardTypeOpen ? 'rotate(180deg)' : 'rotate(0)' }} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {this.state.cardTypeOpen && (
                  <>
                    <div className="tw-fixed tw-inset-0 tw-z-10" onClick={() => this.setState({ cardTypeOpen: false })} />
                    <div className="dropdown-enter tw-absolute tw-left-0 tw-top-full tw-mt-1.5 tw-z-20 tw-rounded-xl tw-py-1.5 tw-min-w-[200px]" style={isDark ? { backgroundColor: 'rgba(30,30,30,0.95)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' } : { backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
                      <button
                        onClick={() => { this.setState({ cardTypeOpen: false }); this.handleFilterChange('cardType', ''); }}
                        className="tw-w-full tw-text-left tw-px-4 tw-py-2.5 tw-text-sm tw-cursor-pointer tw-border-0 tw-flex tw-items-center tw-justify-between tw-bg-transparent"
                        style={!cardType ? { color: '#c9a84c', fontWeight: 600 } : (isDark ? { color: '#fff' } : { color: '#1a1a1a' })}
                      >
                        <span>All Types</span>
                        {!cardType && <svg className="tw-w-4 tw-h-4" style={{ color: '#c9a84c' }} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                      </button>
                      {this.state.cardTypeOptions.map(function(t) {
                        var isActive = cardType === t;
                        return (
                          <button
                            key={t}
                            onClick={function() { this.setState({ cardTypeOpen: false }); this.handleFilterChange('cardType', t); }.bind(this)}
                            className="tw-w-full tw-text-left tw-px-4 tw-py-2.5 tw-text-sm tw-cursor-pointer tw-border-0 tw-flex tw-items-center tw-justify-between tw-bg-transparent"
                            style={isActive ? { color: '#c9a84c', fontWeight: 600 } : (isDark ? { color: '#fff' } : { color: '#1a1a1a' })}
                          >
                            <span>{t}</span>
                            {isActive && <svg className="tw-w-4 tw-h-4" style={{ color: '#c9a84c' }} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                          </button>
                        );
                      }.bind(this))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-3">
            {rarity && (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-brand-gold/10 tw-text-brand-gold tw-border tw-border-brand-gold/30">
                Rarity: {rarity}
                <button onClick={() => this.handleFilterChange('rarity', '')} className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-brand-gold hover:tw-text-brand-gold-dark tw-text-sm tw-leading-none tw-ml-0.5">&times;</button>
              </span>
            )}
            {element && (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-brand-gold/10 tw-text-brand-gold tw-border tw-border-brand-gold/30">
                Element: {element}
                <button onClick={() => this.handleFilterChange('element', '')} className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-brand-gold hover:tw-text-brand-gold-dark tw-text-sm tw-leading-none tw-ml-0.5">&times;</button>
              </span>
            )}
            {cardType && (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-brand-gold/10 tw-text-brand-gold tw-border tw-border-brand-gold/30">
                Type: {cardType}
                <button onClick={() => this.handleFilterChange('cardType', '')} className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-brand-gold hover:tw-text-brand-gold-dark tw-text-sm tw-leading-none tw-ml-0.5">&times;</button>
              </span>
            )}
            {(rarity && element) || (rarity && cardType) || (element && cardType) ? (
              <button
                onClick={() => { this.handleFilterChange('rarity', ''); this.handleFilterChange('element', ''); this.handleFilterChange('cardType', ''); }}
                className="tw-text-xs tw-text-[#999] hover:tw-text-brand-gold tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors"
              >
                Clear all
              </button>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  renderCardGrid() {
    const { cardsLoading, cardsTotal, search } = this.state;
    const cards = this.getFilteredCards();

    if (cardsLoading && cards.length === 0) {
      return (
        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 md:tw-gap-5">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-overflow-hidden">
              <div className="tw-aspect-[3/4] hp-skeleton" />
              <div className="tw-p-3">
                <div className="tw-h-3.5 tw-w-3/4 hp-skeleton tw-rounded tw-mb-1.5" />
                <div className="tw-h-2.5 tw-w-1/2 hp-skeleton tw-rounded tw-mb-1" />
                <div className="tw-h-2.5 tw-w-2/5 hp-skeleton tw-rounded" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (cards.length === 0) {
      const { crossSetResults, crossSetLoading } = this.state;
      return (
        <div className="tw-mt-12">
          <p className="tw-text-center tw-text-[#999] dark:tw-text-gray-500 tw-text-sm">
            No cards match your filters.
          </p>
          {search && crossSetLoading && (
            <p className="tw-text-center tw-text-[#999] dark:tw-text-gray-500 tw-text-xs tw-mt-4">
              Searching other sets...
            </p>
          )}
          {search && crossSetResults.length > 0 && (
            <div className="tw-mt-6">
              <p className="tw-text-center tw-text-[#999] dark:tw-text-gray-500 tw-text-sm tw-mb-4">
                Found in other sets:
              </p>
              <div className="tw-max-w-md tw-mx-auto tw-space-y-2">
                {crossSetResults.slice(0, 5).map(card => {
                  const imageUrl = resolveCardImage(card.image);
                  return (
                    <Link
                      key={card._id}
                      to={`/card/${card._id}`}
                      className="tw-flex tw-items-center tw-gap-3 tw-p-2 tw-rounded-lg tw-bg-white dark:tw-bg-[#1a1a1a] tw-border tw-border-solid tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] hover:tw-border-brand-gold tw-transition-colors"
                      style={{ textDecoration: 'none' }}
                    >
                      <div className="tw-w-10 tw-h-14 tw-rounded tw-overflow-hidden tw-bg-[#f0f0f0] dark:tw-bg-[#252525] tw-flex-shrink-0">
                        {imageUrl && <img src={imageUrl} alt={card.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      </div>
                      <div className="tw-flex-1 tw-min-w-0">
                        <p className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0 tw-truncate">
                          {card.name}
                        </p>
                        <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-mb-0">
                          {card.cardSet}
                        </p>
                      </div>
                      {renderRarityBadge(card.rarity, null)}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }

    var sealedProducts = this.state.sealedProducts || [];

    return (
      <>
        {/* Sealed products at top of set */}
        {sealedProducts.length > 0 && (
          <div className="tw-mb-6">
            <p className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-[#999] tw-mb-3">
              <i className="fa fa-cube tw-mr-1.5" style={{ color: '#7c3aed' }} />Sealed Products
            </p>
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 md:tw-gap-5 tw-items-stretch">
              {sealedProducts.map(function(sp) {
                var spImage = resolveCardImage(sp.image);
                var typeLabel = formatSealedType(sp.sealedType);
                return (
                  <Link
                    key={sp._id}
                    to={'/card/' + sp._id}
                    className="tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-solid tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-overflow-hidden tw-transition-all tw-duration-200 browse-set-hover tw-flex tw-flex-col"
                    style={{ display: 'flex', textDecoration: 'none' }}
                  >
                    <div className="tw-aspect-[3/4] tw-bg-[#f0f0f0] dark:tw-bg-[#252525] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-relative">
                      {spImage ? (
                        <img src={spImage} alt={sp.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <div className="tw-text-center">
                          <i className="fa fa-cube tw-text-4xl" style={{ color: '#7c3aed' }} />
                        </div>
                      )}
                      {/* Sealed badge */}
                      <div
                        className="tw-absolute tw-top-2 tw-left-2 tw-text-white tw-font-bold tw-px-2.5 tw-py-1 tw-rounded-full"
                        style={{ fontSize: '10px', backgroundColor: '#7c3aed', boxShadow: '0 1px 6px rgba(0,0,0,0.4)' }}
                      >
                        {typeLabel}
                      </div>
                      {/* Price badge */}
                      {sp.lowestPrice > 0 && (
                      <div
                        className="tw-absolute tw-bottom-2 tw-left-2 tw-text-brand-gold tw-text-base tw-font-bold tw-px-3 tw-py-1 tw-rounded-full tw-shadow-lg"
                        style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
                      >
                        ${Number(sp.lowestPrice).toFixed(2)}
                      </div>
                      )}
                    </div>
                    <div className="tw-p-3 tw-flex-1">
                      <p className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0.5 tw-truncate">
                        {sp.name}
                      </p>
                      {sp.listingCount > 0 && (
                        <p className="tw-text-xs tw-text-[#666] dark:tw-text-[#999] tw-mb-0 tw-truncate">
                          {sp.listingCount} {sp.listingCount === 1 ? 'listing' : 'listings'}
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 md:tw-gap-5 tw-items-stretch">
          {arrangeCardsForGrid(cards, 4).map(card => {
            const imageUrl = resolveCardImage(card.image);
            const isHoriz = card.horizontal === true;
            const marketPrice = card.lastSalePrice;
            let dealPercent = 0;
            if (marketPrice && card.lowestPrice && card.lowestPrice < marketPrice) {
              dealPercent = Math.round(((marketPrice - card.lowestPrice) / marketPrice) * 100);
            }

            return (
              <Link
                key={card._id}
                to={`/card/${card._id}`}
                className={'tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-solid tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-overflow-hidden tw-transition-all tw-duration-200 browse-set-hover tw-flex tw-flex-col' + (isHoriz ? ' horiz-card' : '')}
                style={{ display: 'flex', textDecoration: 'none' }}
              >
                <div className={isHoriz ? 'horiz-card-img tw-bg-[#f0f0f0] dark:tw-bg-[#252525] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-relative' : 'tw-aspect-[3/4] tw-bg-[#f0f0f0] dark:tw-bg-[#252525] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-py-2 tw-relative'}>
                  <LazyCardImage src={imageUrl} alt={card.name} fallbackSize="tw-w-12 tw-h-12" rotate={isHoriz ? (card.rotation || -90) : 0} />
                  {/* Deal badge (top-right) */}
                  {dealPercent >= 15 ? (
                    <div
                      className="tw-absolute tw-top-2 tw-right-2 tw-text-white tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-full"
                      style={{ fontSize: '11px', backgroundColor: '#059669', boxShadow: '0 1px 6px rgba(0,0,0,0.4)' }}
                    >
                      {dealPercent}% Below Market
                    </div>
                  ) : null}
                  {/* Price badge (bottom-left) */}
                  {card.lowestPrice != null && card.listingCount > 0 ? (
                    <div
                      className="tw-absolute tw-bottom-2 tw-left-2 tw-text-brand-gold tw-text-base tw-font-bold tw-px-3 tw-py-1 tw-rounded-full tw-shadow-lg"
                      style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
                    >
                      ${card.lowestPrice.toFixed(2)}
                    </div>
                  ) : (
                    <div
                      className="tw-absolute tw-bottom-2 tw-left-2 tw-text-xs tw-font-medium tw-px-3 tw-py-1 tw-rounded-full"
                      style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#999' }}
                    >
                      No listings
                    </div>
                  )}
                </div>
                <div className="tw-p-3 tw-flex-1">
                  <p className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0.5 tw-truncate">
                    {card.name}
                  </p>
                  <p className="tw-text-xs tw-text-[#666] dark:tw-text-[#999] tw-mb-0.5 tw-truncate">
                    {card.listingCount > 0
                      ? card.listingCount + (card.listingCount === 1 ? ' listing' : ' listings')
                      : '0 listings'}
                  </p>
                  {marketPrice ? (
                    <p className="tw-text-xs tw-text-[#666] dark:tw-text-[#999] tw-mb-0.5 tw-truncate">
                      Mkt: ${marketPrice.toFixed(2)}
                      {dealPercent > 0 && dealPercent < 15 ? (
                        <span className="tw-text-green-600 tw-font-semibold tw-ml-1">
                          {dealPercent}%
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                  <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-x-1 tw-gap-y-0.5">
                    {(card.errata || card.variant) ? (
                      <div style={{ lineHeight: 1 }}>
                        {card.errata && (
                          <span className="tw-text-orange-600 dark:tw-text-orange-400 tw-font-semibold tw-mr-1.5" style={{ fontSize: '10px' }}>
                            Errata
                          </span>
                        )}
                        {card.variant && (
                          <span className="tw-text-brand-gold tw-font-semibold" style={{ fontSize: '10px' }}>
                            {card.variant}
                          </span>
                        )}
                      </div>
                    ) : <span />}
                    {renderRarityBadge(card.rarity, null)}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Infinite scroll loading indicator */}
        {!search && cards.length < cardsTotal && (
          <div className="tw-text-center tw-mt-8 tw-py-4">
            {cardsLoading ? (
              <div className="tw-inline-block tw-w-6 tw-h-6 tw-border-2 tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-border-t-brand-gold tw-rounded-full tw-animate-spin" />
            ) : (
              <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-mb-0">
                {cards.length} of {cardsTotal} cards
              </p>
            )}
          </div>
        )}
      </>
    );
  }

  render() {
    const params = (this.props.match && this.props.match.params) || {};
    const setName = params.setName;
    const gameSlug = params.gameSlug;
    const decodedSetName = setName ? decodeURIComponent(setName) : null;
    const isSetView = !!setName;
    const { currentGame, isMultiGame } = this.context || {};
    const gameName = (currentGame && currentGame.name) || 'Primal TCG';
    const browseBase = gameSlug ? `/browse/${encodeURIComponent(gameSlug)}` : '/browse';

    return (
      <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-8 md:tw-py-12">
        <style>{`
          a.browse-set-hover:hover {
            border-color: #c9a84c !important;
            box-shadow: 0 6px 20px rgba(0,0,0,0.1) !important;
            text-decoration: none !important;
            transform: translateY(-3px) !important;
          }
          .tw-dark a.browse-set-hover:hover {
            box-shadow: 0 6px 20px rgba(201,168,76,0.15) !important;
          }
          a.browse-set-hover img {
            transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
          }
          a.browse-set-hover:hover img {
            transform: scale(1.04);
          }
        `}</style>

        <Helmet>
          <title>{isSetView ? `${decodedSetName} | Browse` : 'Browse Card Sets'} | Primal Marketplace</title>
          <meta name="description" content={isSetView ? `Browse cards from ${decodedSetName} on Primal Marketplace` : `Browse all ${gameName} card sets on the marketplace`} />
        </Helmet>

        {/* Breadcrumb */}
        <nav className="tw-mb-6 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-pl-0 tw-overflow-hidden tw-w-full">
          <Link to="/" className="tw-flex-shrink-0 hover:tw-text-brand-gold tw-no-underline tw-text-[#999] dark:tw-text-gray-500 tw-transition-colors">
            Home
          </Link>
          <span className="tw-flex-shrink-0">/</span>
          {isMultiGame && gameSlug ? (
            <>
              <Link to="/browse" className="tw-flex-shrink-0 hover:tw-text-brand-gold tw-no-underline tw-text-[#999] dark:tw-text-gray-500 tw-transition-colors">
                Browse
              </Link>
              <span className="tw-flex-shrink-0">/</span>
              {isSetView ? (
                <>
                  <Link to={browseBase} className="tw-flex-shrink-0 hover:tw-text-brand-gold tw-no-underline tw-text-[#999] dark:tw-text-gray-500 tw-transition-colors">
                    {gameName}
                  </Link>
                  <span className="tw-flex-shrink-0">/</span>
                  <span className="tw-text-[#1a1a1a] dark:tw-text-white tw-truncate tw-min-w-0">{decodedSetName}</span>
                </>
              ) : (
                <span className="tw-text-[#1a1a1a] dark:tw-text-white">{gameName}</span>
              )}
            </>
          ) : isSetView ? (
            <>
              <Link to={browseBase} className="tw-flex-shrink-0 hover:tw-text-brand-gold tw-no-underline tw-text-[#999] dark:tw-text-gray-500 tw-transition-colors">
                Browse
              </Link>
              <span className="tw-flex-shrink-0">/</span>
              <span className="tw-text-[#1a1a1a] dark:tw-text-white tw-truncate tw-min-w-0">{decodedSetName}</span>
            </>
          ) : (
            <span className="tw-text-[#1a1a1a] dark:tw-text-white">Browse</span>
          )}
        </nav>

        {/* Title */}
        <h1 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-8">
          {isSetView ? decodedSetName : (!gameSlug && this.context && this.context.isMultiGame) ? 'Choose a Game' : 'Card Sets'}
        </h1>

        {isSetView ? (
          <>
            {this.renderFilterBar()}
            {this.renderCardGrid()}
          </>
        ) : (!gameSlug && this.context && this.context.isMultiGame) ? (
          this.renderGameGrid()
        ) : (
          this.renderSetGrid()
        )}

        {/* Scroll to top button */}
        {this.state.showScrollTop && (
          <button
            onClick={() => this.scrollToTop()}
            className="tw-fixed tw-bottom-6 tw-right-6 tw-w-10 tw-h-10 tw-rounded-full tw-bg-brand-gold tw-text-black tw-border-0 tw-cursor-pointer tw-z-40 tw-flex tw-items-center tw-justify-center mobile-tab-offset-btn"
            style={{ boxShadow: '0 4px 16px rgba(201, 168, 76, 0.3)', animation: 'hp-fadeUp 0.3s ease forwards' }}
            aria-label="Scroll to top"
          >
            <svg className="tw-w-6 tw-h-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    );
  }
}

export default Browse;
